/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable consistent-return */
import notify from "../../utils/notify";
import { authorizedApiClient } from "../config";
import {
  AvailableObject,
  CreateFieldMapping,
  CreateFieldMappingResponse,
  CreateMultipleFieldMappings,
  CreateSyncConfiguration,
  CreateSyncConfigurationResponse,
  CreateWriteBackSync,
  DeleteFieldMapping,
  DeleteSyncConfiguration,
  FieldMapping,
  GetAvailableObjects,
  GetAvailableObjectsResponse,
  GetFieldMappings,
  GetFieldMappingsResponse,
  GetFieldsAndMetrics,
  GetFieldsAndMetricsResponse,
  GetObjectFields,
  GetObjectFieldsResponse,
  GetSyncConfiguration,
  GetSyncConfigurationResponse,
  GetSyncConfigurations,
  GetSyncConfigurationsResponse,
  GetWorkspaceObjects,
  GetWorkspaceObjectsResponse,
  SyncConfiguration,
  UpdateFieldMapping,
  UpdateFieldMappingResponse,
  UpdateSyncConfiguration,
  UpdateSyncConfigurationResponse,
  ValidateSyncConfiguration,
} from "./syncConfigurationEndpoints.interface";

const showErrorAlert = (err: any) => notify({
  heading: "Unknown Error",
  type: "failure",
  messageList: [
    "Kindly contact Hyperengage support to help you resolve this issue.",
  ],
});

// Sync Configuration API Functions
export const getAvailableObjects = async ({ workspace_id }: GetAvailableObjects): Promise<GetAvailableObjectsResponse> => {
  try {
    const response = await authorizedApiClient.get<GetAvailableObjectsResponse>(`/sync-configurations/${workspace_id}/available-objects`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const getFieldsAndMetrics = async ({ workspace_id, object_slug }: GetFieldsAndMetrics): Promise<GetFieldsAndMetricsResponse> => {
  try {
    const response = await authorizedApiClient.get<GetFieldsAndMetricsResponse>(`/sync-configurations/${workspace_id}/available-objects/${object_slug}/fields-and-metrics`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const createSyncConfiguration = async ({ workspace_id, data }: CreateSyncConfiguration): Promise<CreateSyncConfigurationResponse> => {
  try {
    const response = await authorizedApiClient.post<SyncConfiguration>(`/sync-configurations/${workspace_id}/sync-configurations`, data);
    // The API returns SyncConfiguration directly, but we need to wrap it in the expected format
    return { data: response.data };
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const getSyncConfigurations = async ({ workspace_id }: GetSyncConfigurations): Promise<GetSyncConfigurationsResponse> => {
  try {
    const response = await authorizedApiClient.get<GetSyncConfigurationsResponse>(`/sync-configurations/${workspace_id}/sync-configurations`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const getSyncConfiguration = async ({ workspace_id, sync_id }: GetSyncConfiguration): Promise<GetSyncConfigurationResponse> => {
  try {
    const response = await authorizedApiClient.get<GetSyncConfigurationResponse>(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const updateSyncConfiguration = async ({ workspace_id, sync_id, data }: UpdateSyncConfiguration): Promise<UpdateSyncConfigurationResponse> => {
  try {
    const response = await authorizedApiClient.put<UpdateSyncConfigurationResponse>(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}`, data);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const deleteSyncConfiguration = async ({ workspace_id, sync_id }: DeleteSyncConfiguration): Promise<void> => {
  try {
    await authorizedApiClient.delete(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}`);
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const validateSyncConfiguration = async ({ workspace_id, sync_id }: ValidateSyncConfiguration): Promise<any> => {
  try {
    const response = await authorizedApiClient.post(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}/validate`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

// Field Mapping API Functions
export const createFieldMapping = async ({ workspace_id, sync_id, data }: CreateFieldMapping): Promise<CreateFieldMappingResponse> => {
  try {
    const response = await authorizedApiClient.post<CreateFieldMappingResponse>(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}/field-mappings`, data);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const getFieldMappings = async ({ workspace_id, sync_id }: GetFieldMappings): Promise<GetFieldMappingsResponse> => {
  try {
    const response = await authorizedApiClient.get<GetFieldMappingsResponse>(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}/field-mappings`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const updateFieldMapping = async ({ workspace_id, sync_id, mapping_id, data }: UpdateFieldMapping): Promise<UpdateFieldMappingResponse> => {
  try {
    const response = await authorizedApiClient.put<UpdateFieldMappingResponse>(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}/field-mappings/${mapping_id}`, data);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const deleteFieldMapping = async ({ workspace_id, sync_id, mapping_id }: DeleteFieldMapping): Promise<void> => {
  try {
    await authorizedApiClient.delete(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}/field-mappings/${mapping_id}`);
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

// Workspace Objects and Fields API Functions
export const getWorkspaceObjects = async ({ workspace_id }: GetWorkspaceObjects): Promise<GetWorkspaceObjectsResponse> => {
  try {
    const response = await authorizedApiClient.get<GetWorkspaceObjectsResponse>(`/workspace/${workspace_id}/objects`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

export const getObjectFields = async ({ workspace_id, object_slug }: GetObjectFields): Promise<GetObjectFieldsResponse> => {
  try {
    const response = await authorizedApiClient.get<GetObjectFieldsResponse>(`/sync-configurations/${workspace_id}/object/${object_slug}/fields-and-metrics`);
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

// Helper functions for field mapping creation
export const createMultipleFieldMappings = async ({ workspace_id, sync_id, mappings }: CreateMultipleFieldMappings): Promise<FieldMapping[]> => {
  try {
    const promises = mappings.map(mapping =>
      createFieldMapping({ workspace_id, sync_id, data: mapping })
    );
    const results = await Promise.all(promises);
    return results.map(result => result.data);
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

// Bulk field mapping creation
export const createBulkFieldMappings = async ({ workspace_id, sync_id, mappings }: CreateMultipleFieldMappings): Promise<FieldMapping[]> => {
  try {
    const response = await authorizedApiClient.post<{ data: FieldMapping[] }>(`/sync-configurations/${workspace_id}/sync-configurations/${sync_id}/field-mappings/bulk`, mappings);
    return response.data.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};

// Helper to create write-back sync configuration
export const createWriteBackSync = async ({ workspace_id, original_sync, create_missing_records = false }: CreateWriteBackSync): Promise<SyncConfiguration> => {
  try {
    const writeBackData = {
      workspace_id: original_sync.workspace_id,
      integration_id: original_sync.integration_id,
      // For write-back, destination remains the Hyperengage workspace object.
      source_object: original_sync.source_object,
      destination_object: original_sync.destination_object,
      source_object_id: original_sync.source_object_id,
      destination_object_id: original_sync.destination_object_id,
      sync_direction: 'write_out' as const,
      // Create disabled; activation is handled explicitly by the UI flow
      status: 'disabled' as const,
      matcher_field_name: original_sync.matcher_field_name,
      source_matcher_field: original_sync.source_matcher_field || original_sync.matcher_field_name,
      destination_matcher_field: original_sync.destination_matcher_field || 'external_id',
      time_window: original_sync.time_window,
      sync_policy: {
        ...original_sync.sync_policy,
        conflict_resolution: 'source_wins' as const,
      },
    };

    const response = await createSyncConfiguration({ workspace_id, data: writeBackData as any });
    // createSyncConfiguration returns { data: SyncConfiguration }
    return response.data;
  } catch (err) {
    showErrorAlert(err);
    throw err;
  }
};
